import LanguageManager from "./LanguageManager.js";

class ComponentsManager {
    #currentlyLoaded = {}

    /**
     * @author Corentin (ccharton) Charton
     * @description Initialize the ComponentsManager
     */
    constructor() {
        this.currentlyShowed = new Set()
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Check if a component is already loaded and switch its visibility
     * @param name {string} - The name of the component
     * @returns {boolean} - True if the component was loaded, false otherwise
     */
    checkComponentLoaded(name) {
        if (this.#currentlyLoaded[name]) {
            if (this.currentlyShowed.has(this.#currentlyLoaded[name])) {
                this.hideComponent(this.#currentlyLoaded[name])
            } else {
                this.containCenterMenuView(name)
                this.showComponent(this.#currentlyLoaded[name])
                LanguageManager.updateContent()
            }
            return true
        }
        return false
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Load a component into the target selector
     * @param name {string} - The name of the component
     * @param selector {string} - The selector to put the component in
     * @param filePath {string} - The path to the HTML file
     * @param cssPath {string, null} - The path to the CSS file
     * @returns {Promise<void>}
     */
    async loadComponent(name, selector, filePath, cssPath = null) {
        const target = document.querySelector(selector)
        if (!target) {
            return
        }

        if (!this.checkComponentLoaded(name)) {
            try {
                this.#currentlyLoaded[name] = '#' + name
                if (cssPath) {
                    const link = document.createElement("link");
                    link.rel = "stylesheet";
                    link.href = cssPath;
                    document.head.appendChild(link);
                }

                const response = await fetch(filePath)
                if (!response.ok) {
                    throw new Error(`Failed to load ${filePath}`)
                }
                const html = await response.text()
                const tempDiv = document.createElement("div")
                tempDiv.innerHTML = html
                this.containCenterMenuView(name)

                const validChild = Array.from(tempDiv.children).find((elem) => elem.tagName !== "SCRIPT")
                if (validChild) {
                    target.appendChild(validChild)
                    this.#addToState('#' + validChild.id)
                    await LanguageManager.updateContent()
                }
            } catch (error) {
                this.#currentlyLoaded[name] = '#' + name
                console.error(error)
            }
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Check if a sub Center Menu component is already loaded and hide it if
     * @param name {string} - The name of the component loaded
     */
    containCenterMenuView(name) {
        const regexName = /^[a-zA-Z]+CenterMenu/;
        const regexCurrentlyShowed = /^#+[a-zA-Z]+TabCenterMenu/;

        if (regexName.test(name)) {
            const existingMenu = Array.from(this.currentlyShowed).find((comp) => regexCurrentlyShowed.test(comp));

            if (existingMenu) {
                this.hideComponent(existingMenu);
            }
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Hide a component
     * @param selector {string} - The selector of the component to hide
     */
    hideComponent(selector) {
        const element = document.querySelector(selector)

        if (element) {
            element.classList.add("hidden")
            this.#removeFromState(selector)
        }
    }

    /**
     * @description Show a component
     * @param selector {string} - The selector of the component to show
     */
    showComponent(selector) {
        const element = document.querySelector(selector)

        if (element) {
            element.classList.remove("hidden")
            this.#addToState(selector)
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Add a component to the visible components state
     * @param selector {string} - The selector of the component to add
     */
    #addToState(selector) {
        this.currentlyShowed.add(selector)
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Remove a component from the visible components state
     * @param selector {string} - The selector of the component to remove
     */
    #removeFromState(selector) {
        this.currentlyShowed.delete(selector)
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Create and return an array of currently visible components
     * @returns {Array} - The array of currently visible components
     */
    getVisibleComponents() {
        return Array.from(this.currentlyShowed)
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Check if a component is loaded
     * @param name {string} - The name of the component
     * @returns {boolean} - True if the component is loaded, false otherwise
     */
    componentLoaded(name) {
        return this.#currentlyLoaded[name + 'TabCenterMenu'] ? true : !!this.#currentlyLoaded[name + 'CenterMenu'] ;
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Reset the ComponentsManager state
     */
    reset() {
        this.#currentlyLoaded = {}
        this.currentlyShowed = new Set()
    }
}

export default new ComponentsManager()